/**
 * sl-67kc.3.4: WorkspaceOperationsChip
 *
 * Workspace-wide aggregator chip for parked long-running operations
 * (STT today, video / bulk-AI later). Lives in the
 * `WorkspaceAutomationsPanel` header so a user can see in one glance
 * how many automations are waiting on an external job and click
 * through to the History row that owns each one.
 *
 * Visibility:
 *   - Hidden when count is 0 AND the feed has no error — "no waiting
 *     jobs" is silence, not noise. A feed error switches the chip
 *     into an explicit error state instead of silently hiding.
 *
 * Data feed (sl-67kc.3.4 F2):
 *   - Backed by a `LiveData` observable sourced from a 20s polling
 *     timer. There is no dedicated WS channel for suspended-execution
 *     events yet, so the transport is poll; the contract the rest of
 *     the tree sees is a subscription. Refresh-on-mount via the
 *     `startWith(0)` tick so opening the panel always reflects the
 *     latest server state.
 *   - Errors flip a separate `error$` LiveData the chip renders as an
 *     explicit `!` badge with a retry tooltip. The chip does NOT
 *     swallow the error silently — 0-Fallbacks.
 *
 * Deep-link:
 *   - Click dispatches `streamlain:open-automation-history` which the
 *     workspace panel's listener catches to navigate via the workbench,
 *     then re-fires `streamlain:scroll-automation-history` which the
 *     matching `HistoryPanel` uses to scroll the row into view.
 *   - Inline Cancel button per row hits
 *     `POST /api/stt/jobs/:jobId/cancel`. No confirm dialog — the
 *     user already had to open the flyout to see the row; the action
 *     is fast-reversible via Retry.
 *
 * SOLID:
 * - S: Render + subscription. RBAC + data-shaping live server-side in
 *      `WorkspaceRunningOperationsResolver`.
 * - D: Depends on `GraphQLService` for the feed and `WorkbenchService`
 *      indirectly (via CustomEvent → `WorkspaceAutomationsPanel`
 *      listener) for navigation. No direct fetch, no direct router.
 */
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import {
  type WorkspaceRunningOperationsQuery,
  workspaceRunningOperationsQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { type FC, useCallback, useMemo, useState } from 'react';
import { from, interval, startWith, switchMap } from 'rxjs';

import { GraphQLService, workspaceApiFetch } from '../../cloud';
import { WorkspaceService } from '../../workspace';

type WorkspaceRunningOperation =
  WorkspaceRunningOperationsQuery['workspaceRunningOperations'][number];

const POLL_INTERVAL_MS = 20_000;

interface FeedState {
  readonly ops: readonly WorkspaceRunningOperation[];
  readonly error: Error | null;
}

const EMPTY_FEED: FeedState = { ops: [], error: null };

const formatAge = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 60_000) return '<1m';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
};

export const WorkspaceOperationsChip: FC = () => {
  const t = useI18n();
  const gqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;

  // Build the LiveData subscription once per workspaceId. `from(promise)`
  // rejections are forwarded to the LiveData error branch, which the
  // render reads explicitly — no silent degradation.
  const feed$ = useMemo(
    () =>
      LiveData.from<FeedState>(
        interval(POLL_INTERVAL_MS).pipe(
          startWith(0),
          switchMap(() =>
            from(
              gqlService
                .gql({
                  query: workspaceRunningOperationsQuery,
                  variables: { workspaceId },
                })
                .then<FeedState>((result: WorkspaceRunningOperationsQuery) => ({
                  ops: result.workspaceRunningOperations ?? [],
                  error: null,
                }))
                .catch<FeedState>((err: unknown) => ({
                  ops: [],
                  error: err instanceof Error ? err : new Error(String(err)),
                }))
            )
          )
        ),
        EMPTY_FEED
      ),
    [gqlService, workspaceId]
  );
  const { ops, error } = useLiveData(feed$);
  const connectivityStatus = useConnectivityStatus();

  const [open, setOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const sortedOps = useMemo(
    () =>
      [...ops].sort(
        (a, b) =>
          new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
      ),
    [ops]
  );

  const handleRowClick = useCallback((op: WorkspaceRunningOperation) => {
    window.dispatchEvent(
      new CustomEvent('streamlain:open-automation-history', {
        detail: {
          docId: op.docId,
          blockId: op.blockId,
          executionLogId: op.executionLogId,
          suspendedExecutionId: op.suspendedExecutionId,
        },
      })
    );
    setOpen(false);
  }, []);

  const handleCancelClick = useCallback(
    async (op: WorkspaceRunningOperation, e: React.MouseEvent) => {
      e.stopPropagation();
      setCancellingId(op.suspendedExecutionId);
      try {
        const response = await workspaceApiFetch(
          `/api/stt/jobs/${op.jobId}/cancel`,
          { method: 'POST' },
          {
            workspaceId,
            category: 'foreground-action',
            mode: 'foreground',
            priority: 'foreground',
          }
        );
        if (!response.ok) {
          throw new Error(
            `Cancel request failed: ${response.status} ${response.statusText}`
          );
        }
      } catch (err) {
        console.error('[WorkspaceOperationsChip] cancel failed', err);
      } finally {
        setCancellingId(null);
      }
    },
    [workspaceId]
  );

  const backendFeedUnavailable =
    connectivityStatus.offline || isBackendUnavailableError(error);

  // Hide when there is nothing actionable to show. Backend availability is
  // already handled by the global connectivity UI, so this workspace-specific
  // chip must not duplicate it as a noisy feed error.
  if (sortedOps.length === 0 && (!error || backendFeedUnavailable)) return null;

  const chipLabel = error
    ? t['com.streamlain.streamlain.workspace.operations-chip.error-label']()
    : t['com.streamlain.streamlain.workspace.operations-chip.label']({
        count: String(sortedOps.length),
      });
  const chipTooltip = error
    ? t['com.streamlain.streamlain.workspace.operations-chip.error-tooltip']()
    : t['com.streamlain.streamlain.workspace.operations-chip.tooltip']({
        count: String(sortedOps.length),
      });

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={chipTooltip}
        style={{
          padding: '4px 10px',
          fontSize: '11px',
          fontWeight: 600,
          borderRadius: '999px',
          border: `1px solid ${error ? 'var(--streamlain-error-color)' : 'var(--streamlain-primary-color)'}`,
          color: error
            ? 'var(--streamlain-error-color)'
            : 'var(--streamlain-primary-color)',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <span aria-hidden>{error ? '!' : '⏳'}</span>
        <span>{chipLabel}</span>
      </button>
      {open && !error && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '6px',
            backgroundColor: 'var(--streamlain-background-secondary-color)',
            border: '1px solid var(--streamlain-border-color)',
            borderRadius: '6px',
            padding: '6px',
            minWidth: '280px',
            maxWidth: '380px',
            maxHeight: '320px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {sortedOps.map(op => {
            const titleLabel =
              op.automationTitle ??
              t[
                'com.streamlain.streamlain.workspace.operations-chip.untitled-automation'
              ]();
            const docLabel =
              op.docTitle ??
              t[
                'com.streamlain.streamlain.workspace.operations-chip.untitled-doc'
              ]();
            const isCancelling = cancellingId === op.suspendedExecutionId;
            return (
              <div
                key={op.suspendedExecutionId}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  padding: '8px',
                  marginBottom: '4px',
                  borderRadius: '4px',
                }}
              >
                <button
                  onClick={() => handleRowClick(op)}
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'inherit',
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '2px',
                    }}
                  >
                    <span
                      style={{
                        padding: '1px 5px',
                        fontSize: '9px',
                        fontWeight: 600,
                        borderRadius: '3px',
                        backgroundColor:
                          op.status === 'pending'
                            ? 'var(--streamlain-primary-color)'
                            : 'var(--streamlain-warning-color)',
                        color: 'white',
                        textTransform: 'uppercase',
                      }}
                    >
                      {op.status}
                    </span>
                    <span style={{ opacity: 0.65, fontSize: '11px' }}>
                      {formatAge(op.submittedAt)}
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '11px' }}>
                    {titleLabel}
                  </div>
                  <div style={{ opacity: 0.7, fontSize: '11px' }}>
                    {docLabel}
                  </div>
                </button>
                <button
                  onClick={e => {
                    void handleCancelClick(op, e);
                  }}
                  disabled={isCancelling}
                  title={t[
                    'com.streamlain.streamlain.workspace.operations-chip.cancel-tooltip'
                  ]()}
                  style={{
                    padding: '3px 8px',
                    fontSize: '10px',
                    fontWeight: 600,
                    borderRadius: '4px',
                    border: '1px solid var(--streamlain-border-color)',
                    backgroundColor: 'transparent',
                    color: isCancelling
                      ? 'var(--streamlain-text-secondary-color)'
                      : 'var(--streamlain-error-color)',
                    cursor: isCancelling ? 'wait' : 'pointer',
                    alignSelf: 'flex-start',
                  }}
                >
                  {isCancelling
                    ? t[
                        'com.streamlain.streamlain.workspace.operations-chip.cancelling'
                      ]()
                    : t[
                        'com.streamlain.streamlain.workspace.operations-chip.cancel'
                      ]()}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
